import { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { AppProvider } from './context';
import SyncScreen from './components/SyncScreen';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ContactList from './components/ContactList';
import ContactDetail from './components/ContactDetail';
import LogInteraction from './components/LogInteraction';
import Activity from './components/Activity';
import Profile from './components/Profile';
import AddContact from './components/AddContact';
import WeeklyGoal from './components/WeeklyGoal';
import SplashScreen from './components/SplashScreen';
import Onboarding from './components/Onboarding';
import Notifications from './components/Notifications';
import NotificationSettings from './components/NotificationSettings';
import EditContact from './components/EditContact';
import DeleteContact from './components/DeleteContact';
import ForgotPassword from './components/ForgotPassword';
import PersonalInfo from './components/PersonalInfo';
import EditField from './components/EditField';
import PrivacySecurity from './components/PrivacySecurity';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import HelpSupport from './components/HelpSupport';
import TicketThread from './components/TicketThread';
import ScrollToTop from './components/ScrollToTop';

import { useApp } from './context';
import Welcome from './components/Welcome';
import Login from './components/Login';
import { isOAuthRedirectPending } from './lib/firebase';
import SuggestedInteractionModal from './components/SuggestedInteractionModal';
import ContactHistory from './components/ContactHistory';
import ImportContacts from './components/ImportContacts';
import DeveloperSettings from './components/DeveloperSettings';
import { StorageFullModal } from './components/StorageFullModal';
import { UndoToast } from './components/UndoToast';
import { Toast } from './components/Toast';
import NetworkStatusBar from './components/NetworkStatusBar';
import { PWAUpdatePrompt } from './components/PWAUpdatePrompt';
import { InstallPrompt } from './components/InstallPrompt';

const DEVELOPER_ROLES = ['tester', 'admin', 'super_admin'];

function DeveloperGuard() {
  const { state } = useApp();
  const navigate = useNavigate();
  const hasAccess = state.user?.roles?.some((r) => DEVELOPER_ROLES.includes(r)) ?? false;

  useEffect(() => {
    if (!hasAccess) navigate('/profile', { replace: true });
  }, [hasAccess, navigate]);

  if (!hasAccess) return null;
  return <DeveloperSettings />;
}

function AppContent() {
  const { state, clearSyncPending } = useApp();
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  const oauthReturning = isOAuthRedirectPending();
  const [showWelcome, setShowWelcome] = useState(!state.user && !oauthReturning);
  const [showLogin, setShowLogin] = useState(oauthReturning);
  const [loginSource, setLoginSource] = useState<'welcome' | 'guest'>('welcome');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [legalPage, setLegalPage] = useState<'privacy' | 'terms' | null>(null);

  // Capture referral code from URL (?ref=XXXXXXXX) into localStorage + cookie.
  // Cookie is needed because PWA standalone mode has separate localStorage from Safari.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      const code = ref.toUpperCase();
      localStorage.setItem('nudge_referral_code', code);
      document.cookie = `nudge_ref=${code}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
      // Clean URL without reload
      params.delete('ref');
      const clean = params.toString();
      window.history.replaceState({}, '', clean ? `?${clean}` : window.location.pathname);
    } else if (!localStorage.getItem('nudge_referral_code')) {
      // PWA standalone launch: recover referral code from cookie if localStorage is empty
      const match = document.cookie.match(/(?:^|; )nudge_ref=([A-Z0-9]+)/);
      if (match) {
        localStorage.setItem('nudge_referral_code', match[1]);
      }
    }
  }, []);

  useEffect(() => {
    const handleOpenLogin = (e: any) => {
      setLoginSource(e.detail?.source || 'welcome');
      setShowLogin(true);
    };
    window.addEventListener('open-login', handleOpenLogin);
    return () => window.removeEventListener('open-login', handleOpenLogin);
  }, []);

  // Listen for push notification clicks from service worker
  useEffect(() => {
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
  }, [navigate]);

  useEffect(() => {
    if (!state.user && !showSplash && !showOnboarding && !showLogin) {
      setShowWelcome(true);
    }
  }, [state.user, showSplash, showOnboarding, showLogin]);

  const handleSplashFinish = useCallback(() => {
    setShowSplash(false);
  }, []);

  const handleWelcomeComplete = useCallback(() => {
    setShowWelcome(false);
    setShowOnboarding(true);
  }, []);

  const handleWelcomeLogin = useCallback(() => {
    setShowWelcome(false);
    setLoginSource('welcome');
    setShowLogin(true);
  }, []);

  const handleLoginBack = useCallback(() => {
    setShowLogin(false);
    if (loginSource === 'welcome') {
      setShowWelcome(true);
    }
  }, [loginSource]);

  const handleLoginComplete = useCallback(
    (isNewUser: boolean) => {
      setShowLogin(false);
      window.scrollTo(0, 0);
      if (loginSource === 'welcome' && isNewUser) {
        setShowOnboarding(true);
      } else {
        navigate('/');
      }
    },
    [loginSource, navigate],
  );

  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    window.scrollTo(0, 0);
    navigate('/');
  }, [navigate]);

  return (
    <>
      {showSplash && <SplashScreen onFinish={handleSplashFinish} />}
      {!showSplash && showWelcome && !showLogin && (
        <Welcome
          onComplete={handleWelcomeComplete}
          onLogin={handleWelcomeLogin}
          onShowLegal={setLegalPage}
        />
      )}
      {showLogin && (
        <Login
          onBack={handleLoginBack}
          onComplete={handleLoginComplete}
          onShowLegal={setLegalPage}
        />
      )}

      {/* Legal pages overlay (pre-auth) */}
      {legalPage && (
        <div className="fixed inset-0 z-100 bg-[#f8f5f5] overflow-y-auto">
          {legalPage === 'privacy' && <PrivacyPolicy onBack={() => setLegalPage(null)} />}
          {legalPage === 'terms' && <TermsOfService onBack={() => setLegalPage(null)} />}
        </div>
      )}
      {showOnboarding && <Onboarding onComplete={handleOnboardingComplete} />}

      {state.syncPending && !showSplash && !showOnboarding && !showLogin && (
        <SyncScreen
          guestContacts={state.syncPending.contacts}
          guestInteractions={state.syncPending.interactions}
          guestWeeklyGoal={state.syncPending.weeklyGoal}
          onComplete={(freshData) => {
            clearSyncPending(freshData);
            navigate('/');
          }}
          onSkip={() => clearSyncPending()}
        />
      )}

      {!showSplash && !showWelcome && !showLogin && !showOnboarding && (
        <>
          <ScrollToTop />
          <NetworkStatusBar />
          <Layout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/contacts" element={<ContactList />} />
              <Route path="/contact/:id" element={<ContactDetail />} />
              <Route path="/contact/:id/history" element={<ContactHistory />} />
              <Route path="/log" element={<LogInteraction />} />
              <Route path="/add-contact" element={<AddContact />} />
              <Route path="/import-contacts" element={<ImportContacts />} />
              <Route path="/weekly-goal" element={<WeeklyGoal />} />
              <Route path="/activity" element={<Activity />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/notification-settings" element={<NotificationSettings />} />
              <Route path="/edit-contact/:id" element={<EditContact />} />
              <Route path="/delete-contact/:id" element={<DeleteContact />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/personal-info" element={<PersonalInfo />} />
              <Route path="/personal-info/edit/:field" element={<EditField />} />
              <Route path="/privacy-security" element={<PrivacySecurity />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/help-support" element={<HelpSupport />} />
              <Route path="/support/:id" element={<TicketThread />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/developer" element={<DeveloperGuard />} />
            </Routes>
          </Layout>
          <SuggestedInteractionModal />
          <StorageFullModal />
          <UndoToast />
          <Toast />
          <PWAUpdatePrompt />
        </>
      )}
      {!showSplash && <InstallPrompt />}
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Router>
        <AppContent />
      </Router>
    </AppProvider>
  );
}
